// A boat's anodes, one line per anode (docs/map-work-plan.md, session 5). Pure: no Firebase, no DOM
// (tests/unit/anodes.test.mjs); the functions read it too (functions/shared, through boatDataCore).
//
// On the boat (customers/{cid}/Boats/{bid}):
//   Anodes/{aid} { Location, Material, Size, Qty, Description, Order, Source: { Kind, By, At },
//                  Part_ID, Part_Number, Last_Replaced }   (part and last replaced: session 6)
//   Anode_List   the old free text ("2× trim tab anode · aluminum"). Until a boat has lines, its lines
//                are read from this text (Source Kind 'text', not stored); the first save stores them.
//                Every save writes the text back from the lines, so pages still reading it stay right.

export const ANODE_LOCATIONS = [
  { key: 'shaft', EN: 'Shaft collar', ES: 'Collar del eje', words: /shaft/i },
  { key: 'prop', EN: 'Prop nut', ES: 'Tuerca de la hélice', words: /\bprop\b|propeller/i },
  { key: 'rudder', EN: 'Rudder plate', ES: 'Placa del timón', words: /rudder/i },
  { key: 'trimtab', EN: 'Trim tab', ES: 'Flap de trim', words: /trim tab/i },
  { key: 'outdrive', EN: 'Outdrive ring / bearing carrier', ES: 'Anillo / portarrodamiento de la cola', words: /bearing carrier|outdrive|\bring\b(?!.*saildrive)/i },
  { key: 'cavitation', EN: 'Cavitation plate', ES: 'Placa anticavitación', words: /cavitation/i },
  { key: 'trimcyl', EN: 'Trim cylinders', ES: 'Cilindros de trim', words: /trim cyl/i },
  { key: 'saildrive', EN: 'Saildrive ring', ES: 'Anillo del saildrive', words: /saildrive/i },
  { key: 'gearcase', EN: 'Gearcase', ES: 'Caja de engranajes', words: /gear ?case|lower unit/i },
  { key: 'bracket', EN: 'Bracket', ES: 'Soporte', words: /bracket|transom/i },
  { key: 'hull', EN: 'Hull plate', ES: 'Placa del casco', words: /hull|plate/i },
  { key: 'thruster', EN: 'Bow thruster', ES: 'Hélice de proa', words: /thruster/i },
  { key: 'engine', EN: 'Engine pencil', ES: 'Lápiz del motor', words: /pencil|engine/i },
  { key: 'kit', EN: 'Anode kit', ES: 'Kit de ánodos', words: /\bkit\b/i },
  { key: 'other', EN: 'Other', ES: 'Otro', words: null },
];
const LOC = Object.fromEntries(ANODE_LOCATIONS.map(l => [l.key, l]));
// Checked in this order: the specific words before the general ones (a "trim tab" isn't a "plate").
const LOC_ORDER = ['kit', 'saildrive', 'trimtab', 'trimcyl', 'cavitation', 'thruster', 'shaft', 'prop', 'rudder', 'outdrive', 'gearcase', 'bracket', 'engine', 'hull'];

export const ANODE_MATERIALS = [
  { key: 'zinc', EN: 'Zinc', ES: 'Zinc' },
  { key: 'aluminum', EN: 'Aluminum', ES: 'Aluminio' },
  { key: 'magnesium', EN: 'Magnesium', ES: 'Magnesio' },
];
const MATERIAL_WORD = /^(zinc|aluminum|aluminium|magnesium)$/i;
const normMaterial = m => { const s = String(m || '').trim().toLowerCase(); return s === 'aluminium' ? 'aluminum' : (ANODE_MATERIALS.some(x => x.key === s) ? s : ''); };

export const locationLabel = (key, lang = 'EN') => (LOC[key] || LOC.other)[lang === 'ES' ? 'ES' : 'EN'];
export const materialLabel = (key, lang = 'EN') => ANODE_MATERIALS.find(m => m.key === key)?.[lang === 'ES' ? 'ES' : 'EN'] || '';

/** Which location a piece of text names ('other' when none). */
export function locationOf(text) {
  const t = String(text || '');
  return LOC_ORDER.find(k => LOC[k].words.test(t)) || 'other';
}

const cleanQty = q => { const n = parseInt(q, 10); return n > 0 && n < 100 ? n : 1; };

/**
 * The old free text read as lines: [{ Location, Material, Size, Qty, Description }]. Pieces are split on
 * " · ", ";", " + " and new lines; "2×" (or "2x") is the count; a piece that is only a material is the
 * material of every line; "(1¼″)" is a size; an anode kit keeps its words as the description.
 */
export function parseAnodeText(text) {
  const pieces = String(text || '').split(/\s*(?:·|;|\n|\s\+\s)\s*/).map(s => s.trim()).filter(Boolean);
  const material = normMaterial(pieces.find(p => MATERIAL_WORD.test(p)));
  return pieces.filter(p => !MATERIAL_WORD.test(p)).map((p) => {
    const m = p.match(/^(\d+)\s*[×x]\s*(.+)$/i);
    const qty = m ? cleanQty(m[1]) : 1;
    let rest = (m ? m[2] : p).trim();
    const size = (rest.match(/\(([^()]*?\d[^()]*?(?:["″”]|in\b|inch|mm)[^()]*)\)/i) || [])[1] || '';
    if (size) rest = rest.replace(`(${size})`, '').replace(/\s{2,}/g, ' ').trim();
    const ownMaterial = normMaterial((rest.match(/\b(zinc|aluminum|aluminium|magnesium)\b/i) || [])[1]);
    const Location = locationOf(rest);
    const line = { Location, Material: ownMaterial || material, Size: size.trim(), Qty: qty, Description: '' };
    // Keep the words when the location alone doesn't say it (a kit's make and parts, an odd one).
    if (Location === 'kit' || Location === 'other') line.Description = rest.slice(0, 120);
    return line;
  });
}

/** The boat's anode lines, sorted: [{ aid, ...line }]. Read from Anode_List (not stored, Source text) until it has some. */
export function anodesOf(boat) {
  const stored = Object.entries(boat?.Anodes || {}).filter(([, a]) => a && typeof a === 'object');
  if (stored.length) {
    return stored.map(([aid, a]) => ({ aid, ...a, Qty: cleanQty(a.Qty) }))
      .sort((x, y) => (x.Order ?? 0) - (y.Order ?? 0) || x.aid.localeCompare(y.aid));
  }
  return parseAnodeText(boat?.Anode_List).map((l, i) => ({ aid: textAnodeId(i + 1), ...l, Order: i + 1, Source: { Kind: 'text' }, fromText: true }));
}

/** Ids for lines read from the text: the same on every device, so two people moving one boat's list in agree. */
export const textAnodeId = n => `t${n}`;
export const newAnodeId = () => `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

/** Has the boat stored lines, every one entered or confirmed by a person (or the catalog)? */
export function anodesKnown(boat) {
  const stored = Object.values(boat?.Anodes || {}).filter(a => a && typeof a === 'object');
  return stored.length > 0 && stored.every(a => a.Source?.Kind && a.Source.Kind !== 'text' && !(a.Source.Kind === 'ai' && !a.Source.Verified));
}

/** "2× Shaft collar · 1¼″ · aluminum" */
export function lineLabel(line, lang = 'EN') {
  const what = line.Location === 'kit' || line.Location === 'other' ? (line.Description || locationLabel(line.Location, lang)) : locationLabel(line.Location, lang);
  return [`${cleanQty(line.Qty)}× ${what}`, line.Size, materialLabel(line.Material, lang).toLowerCase()].filter(Boolean).join(' · ');
}

/** "5 anodes · aluminum" / "No anodes recorded". */
export function anodeSummary(boat, lang = 'EN') {
  const lines = anodesOf(boat);
  const es = lang === 'ES';
  if (!lines.length) return es ? 'Sin ánodos registrados' : 'No anodes recorded';
  const n = lines.reduce((s, l) => s + cleanQty(l.Qty), 0);
  const mats = [...new Set(lines.map(l => l.Material).filter(Boolean))];
  return [`${n} ${es ? (n === 1 ? 'ánodo' : 'ánodos') : (n === 1 ? 'anode' : 'anodes')}`, mats.map(m => materialLabel(m, lang).toLowerCase()).join(' / ')].filter(Boolean).join(' · ');
}

/** The old text written back from the lines, in the form the pages still reading it understand. */
export function anodeText(lines) {
  const list = (lines || []).filter(Boolean);
  if (!list.length) return '';
  const mats = [...new Set(list.map(l => l.Material).filter(Boolean))];
  const one = mats.length === 1 ? mats[0] : '';
  const parts = list.map((l) => {
    const what = l.Location === 'kit' || l.Location === 'other'
      ? (l.Description || locationLabel(l.Location)) : `${locationLabel(l.Location).toLowerCase()} anode`;
    return `${cleanQty(l.Qty)}× ${what}${l.Size ? ` (${l.Size})` : ''}${!one && l.Material ? ` ${l.Material}` : ''}`;
  });
  return [...parts, ...(one ? [one] : [])].join(' · ');
}

const FIELDS = ['Location', 'Material', 'Size', 'Qty', 'Description', 'Order', 'Part_ID', 'Part_Number', 'Last_Replaced'];

/** A line made safe to store: known location and material, a count, short text. */
export function cleanLine(line, order) {
  const out = {
    Location: LOC[line?.Location] ? line.Location : 'other',
    Material: normMaterial(line?.Material) || null,
    Size: String(line?.Size || '').trim().slice(0, 30) || null,
    Qty: cleanQty(line?.Qty),
    Description: String(line?.Description || '').trim().slice(0, 120) || null,
    Order: order,
  };
  ['Part_ID', 'Part_Number', 'Last_Replaced'].forEach((k) => { if (line?.[k] != null && line[k] !== '') out[k] = line[k]; });
  return out;
}

/**
 * Path updates (relative to the boat) that store `lines` as the boat's whole anode list: each line gets
 * the stamp as its source, lines no longer in it are removed, and Anode_List is written back. A line
 * keeps its aid (and its source, unless changed) when it has one. stamp: { Kind, By, At, Verified? }.
 */
export function anodeSetUpdates(boat, lines, stamp) {
  const updates = {};
  const before = Object.keys(boat?.Anodes || {});
  const keep = new Set();
  const src = { Kind: stamp.Kind || 'tech', By: stamp.By || '', At: stamp.At, ...(stamp.Verified ? { Verified: true } : {}) };
  const kept = [];
  (lines || []).forEach((l, i) => {
    const aid = l.aid || newAnodeId();
    keep.add(aid);
    const line = cleanLine(l, i + 1);
    Object.entries(line).forEach(([k, v]) => { updates[`Anodes/${aid}/${k}`] = v; });
    FIELDS.forEach((k) => { if (!(k in line)) updates[`Anodes/${aid}/${k}`] = null; });
    updates[`Anodes/${aid}/Source`] = src;
    kept.push(line);
  });
  before.forEach((aid) => { if (!keep.has(aid)) updates[`Anodes/${aid}`] = null; });
  updates.Anode_List = anodeText(kept) || null;
  updates['Spec_Skip/Anodes'] = null;
  return updates;
}

/**
 * Path updates for one line's change (the boat page): change = { fields } for line aid (a new aid adds a
 * line) or { remove: true }. Only fields that differ from what's stored are written. A boat still reading
 * its lines from the text has them all stored at the same time (Source text), so changing one doesn't
 * lose the others. Anode_List is written back.
 */
export function anodeLineUpdates(boat, aid, change, stamp) {
  const lines = anodesOf(boat).map(l => ({ ...l }));
  const src = { Kind: stamp.Kind || 'office', By: stamp.By || '', At: stamp.At };
  let i = lines.findIndex(l => l.aid === aid);
  if (change.remove) { if (i >= 0) lines.splice(i, 1); }
  else {
    if (i < 0) { lines.push({ aid, Qty: 1, Location: 'other' }); i = lines.length - 1; }
    Object.assign(lines[i], change.fields || {}, { Source: src, changed: true });
  }
  const updates = {};
  lines.forEach((l, n) => {
    const stored = boat?.Anodes?.[l.aid];
    const line = cleanLine(l, n + 1);
    Object.entries(line).forEach(([k, v]) => { if (!stored || (stored[k] ?? null) !== v) updates[`Anodes/${l.aid}/${k}`] = v; });
    if (stored) FIELDS.forEach((k) => { if (!(k in line) && stored[k] != null) updates[`Anodes/${l.aid}/${k}`] = null; });
    if (l.changed) updates[`Anodes/${l.aid}/Source`] = src;
    else if (!stored) updates[`Anodes/${l.aid}/Source`] = { Kind: 'text', By: stamp.By || '', At: stamp.At };
  });
  if (change.remove && boat?.Anodes?.[aid]) updates[`Anodes/${aid}`] = null;
  updates.Anode_List = anodeText(lines) || null;
  updates['Spec_Skip/Anodes'] = null;
  return updates;
}

// ─── The model catalog: a whole anode set as one value ("2|shaft|aluminum|1¼″;1|prop|aluminum|") ───

export function anodeSetKey(lines) {
  return (lines || []).map(l => [cleanQty(l.Qty), LOC[l.Location] ? l.Location : 'other', normMaterial(l.Material), String(l.Size || '').replace(/[|;]/g, ' ').trim(),
    (l.Location === 'kit' || l.Location === 'other') ? String(l.Description || '').replace(/[|;]/g, ' ').trim() : ''].join('|'))
    .sort().join(';');
}

export function parseAnodeSetKey(key) {
  return String(key || '').split(';').filter(Boolean).map((s) => {
    const [qty, Location, Material, Size, Description] = s.split('|');
    return { Qty: cleanQty(qty), Location: LOC[Location] ? Location : 'other', Material: normMaterial(Material), Size: Size || '', Description: Description || '' };
  });
}

/** "5 anodes: 2× Shaft collar, 1× Prop nut…" for a catalog set. */
export function anodeSetText(key, lang = 'EN') {
  const lines = parseAnodeSetKey(key);
  const n = lines.reduce((s, l) => s + l.Qty, 0);
  const es = lang === 'ES';
  return `${n} ${es ? (n === 1 ? 'ánodo' : 'ánodos') : (n === 1 ? 'anode' : 'anodes')}: ${lines.map(l => lineLabel(l, lang)).join(', ')}`;
}

// ─── Parts and replacement (session 6) ─────────────────────────────────────

const partWords = p => `${p?.Description || ''} ${p?.Manufacturer || ''} ${p?.Part_Number || ''}`;
const isAnodePart = p => /anode|zinc|\bkit\b/i.test(partWords(p));
// "1¼″", "1-1/4\"", "1.25 in" → 1.25; "63mm" → "63mm".
function sizeValue(text) {
  const s = String(text || '').replace(/[″”"]/g, '"');
  const mm = s.match(/(\d+(?:\.\d+)?)\s*mm/i);
  if (mm) return `${mm[1]}mm`;
  const frac = { '¼': 0.25, '½': 0.5, '¾': 0.75, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875 };
  const m = s.match(/(\d+)?\s*(?:-?\s*(\d+)\/(\d+)|([¼½¾⅛⅜⅝⅞]))?\s*(?:"|in\b|inch)/i);
  if (!m || (!m[1] && !m[2] && !m[4])) return null;
  const whole = Number(m[1] || 0);
  const part = m[4] ? frac[m[4]] : m[2] ? Number(m[2]) / Number(m[3]) : 0;
  return Math.round((whole + part) * 1000) / 1000 || null;
}

/**
 * The inventory part a line most likely is, or null: an anode or zinc part whose words name the line's
 * place, with the material and the size counting for it (a "1-1/4\" COLLAR ZINC" for a 1¼″ zinc shaft
 * collar). Only a suggestion; the office links it.
 */
export function suggestAnodePart(line, parts) {
  const loc = LOC[line?.Location];
  if (!loc || !loc.words || line.Location === 'other') return null;
  const size = sizeValue(line.Size);
  let best = null;
  for (const p of parts || []) {
    if (!p || !p.id || !isAnodePart(p)) continue;
    const text = partWords(p);
    if (!loc.words.test(text) && !(line.Location === 'shaft' && /collar|donut/i.test(text))) continue;
    let score = 1;
    if (line.Material === 'zinc' && /\bzinc\b/i.test(text)) score += 1;
    if (line.Material === 'aluminum' && /alum/i.test(text)) score += 1;
    if (line.Material === 'magnesium' && /magnes/i.test(text)) score += 1;
    const ps = sizeValue(text);
    if (size !== null && ps !== null) { if (ps === size) score += 2; else continue; }
    if (line.Location === 'kit' && line.Description) {
      const want = String(line.Description).toLowerCase().split(/[^a-z0-9]+/).filter(w => w.length > 2 && !['anode', 'kit', 'and'].includes(w));
      score += want.filter(w => text.toLowerCase().includes(w)).length;
    }
    if (!best || score > best.score) best = { part: p, score };
  }
  return best ? best.part : null;
}

/** "CAM-X7 · 1-1/2\" COLLAR ZINC" for a part. */
export const partText = p => [p?.Part_Number, p?.Description].map(v => String(v || '').trim()).filter(Boolean).join(' · ');

// "Trim tab anode · 1¼″ · zinc": a line as an item to buy when it has no part.
function typedNeed(l) {
  const words = l.Location === 'kit' || l.Location === 'other';
  const what = words ? (l.Description || locationLabel(l.Location)) : `${locationLabel(l.Location)} anode`;
  return [what, l.Size, materialLabel(l.Material).toLowerCase()].filter(Boolean).join(' · ');
}

/**
 * What a list of lines needs, by part where one is linked: [{ key, partId, partNumber, label, qty }].
 * byId(partId) → the inventory part, if this page has it (the line's own Part_Number is used otherwise).
 */
export function anodeNeeds(lines, byId = () => null) {
  const out = new Map();
  for (const l of lines || []) {
    const part = l.Part_ID ? byId(l.Part_ID) : null;
    const pn = part?.Part_Number || l.Part_Number || '';
    const what = typedNeed(l);
    const key = l.Part_ID ? `p:${l.Part_ID}` : `t:${what.toLowerCase()}`;
    const label = l.Part_ID ? (part ? partText(part) : `${pn} · ${what}`) : what;
    const e = out.get(key) || { key, partId: l.Part_ID || null, partNumber: pn, label, qty: 0 };
    e.qty += cleanQty(l.Qty);
    out.set(key, e);
  }
  return [...out.values()];
}

/** The material most of the boat's anodes are ('' when none says): what Work mode groups anode runs by. */
export function mainMaterial(boat) {
  const tally = {};
  anodesOf(boat).forEach(l => { if (l.Material) tally[l.Material] = (tally[l.Material] || 0) + cleanQty(l.Qty); });
  return Object.entries(tally).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
}

/**
 * Path updates that record the anodes replaced on `day` (YYYY-MM-DD): Last_Replaced on every line (a boat
 * still reading its lines from the text has them stored, Source text). Nothing when it has no lines or
 * they already say that day.
 */
export function replacedUpdates(boat, day, stamp = {}) {
  const lines = anodesOf(boat);
  const updates = {};
  lines.forEach((l, n) => {
    if (l.fromText) {
      Object.entries(cleanLine(l, n + 1)).forEach(([k, v]) => { updates[`Anodes/${l.aid}/${k}`] = v; });
      updates[`Anodes/${l.aid}/Source`] = { Kind: 'text', By: stamp.By || '', At: stamp.At || 0 };
    }
    if (l.Last_Replaced !== day) updates[`Anodes/${l.aid}/Last_Replaced`] = day;
  });
  return Object.keys(updates).length ? updates : null;
}

/** Is this op a Replace Anodes job? By its own name (the op code title), as the questions read it. */
export function isAnodeJob(op) {
  const desc = String(op?.Description || '').trim();
  const name = desc ? desc.split(/\s*\(|\s+:|:\s/)[0] : String(op?.Short_Description_DM || op?.Short_Description || '');
  return /\banodes?\b|\bzincs?\b/i.test(name);
}

/** The lines for reading, one after another: "2× Shaft collar · 1¼″ · aluminum; 1× Prop nut · aluminum". */
export const anodesText = (boat, lang = 'EN') => anodesOf(boat).map(l => lineLabel(l, lang)).join('; ');

/** The latest day any of the boat's anodes was replaced (YYYY-MM-DD), or ''. */
export const lastReplaced = boat => anodesOf(boat).map(l => String(l.Last_Replaced || '')).filter(Boolean).sort().pop() || '';
